import {
  Stack,
  Link,
  HStack,
  Text,
  Alert,
  AlertIcon,
  AlertDescription,
} from "@chakra-ui/react";
import { Link as RouterLink, useActionData } from "@remix-run/react";
import type { ActionArgs, MetaFunction } from "@remix-run/node";
import { T, t } from "~/libs/i18n";
import { withZod } from "@remix-validated-form/with-zod";
import { z } from "zod";
import { ValidatedForm } from "remix-validated-form";
import { ValidatedInput } from "~/components/forms/validated-inputs.tsx";
import { SubmitButton } from "~/components/forms/form-buttons.tsx";
import { parseRequestBody } from "~/utils/request-parser.ts";
import type { FormValidationErrorResponse } from "types/remix";
import { IdentityApi } from "~/api/identity-api.ts";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { ok, serverError } from "~/utils/response-handlers.ts";

export const meta: MetaFunction = () => {
  return [{ title: `${t("Password reset")} - Preventure` }];
};

export const formValidator = withZod(
  z.object({
    email: z
      .string()
      .min(1, { message: "Email is required" })
      .email("Must be a valid email"),
  }),
);

export async function action({ request, params, context }: ActionArgs) {
  const body = await parseRequestBody(request);
  const submission = await formValidator.validate(body);

  if (submission.error) {
    const validationError: FormValidationErrorResponse = {
      type: "FORM_VALIDATION_ERROR",
      ...submission.error,
    };
    return validationError;
  }

  const { email } = submission.data;

  const response = await IdentityApi.InitiatePasswordReset(request, email);
  if (!response.ok) {
    if (response.error.code === "unverified_email_address") {
      await IdentityApi.InitiateEmailVerification(request, { email });
    }
    return serverError({ error: response.error });
  }

  return ok(request, { data: response.data });
}

export default function AuthPasswordReset() {
  const actionData = useActionData<typeof action>();
  const [parent] = useAutoAnimate();

  return (
    <Stack mb={8} ref={parent}>
      <Text textStyle={"heading"}>
        <T>Reset your password</T>
      </Text>
      {actionData && (
        <Alert status="info" data-testid={"alert-message"}>
          <AlertIcon />
          <AlertDescription>
            <T>
              Please check your inbox for further instructions on how to reset
              your password.
            </T>
          </AlertDescription>
        </Alert>
      )}
      <Form />
    </Stack>
  );
}

function Form() {
  return (
    <ValidatedForm validator={formValidator} method={"post"}>
      <Stack>
        <ValidatedInput
          name={"email"}
          type={"email"}
          label={t("Email")}
          helpText={t("We will send you steps to reset your password")}
        />

        <Stack
          w={"full"}
          alignItems={"center"}
          spacing={4}
          px={{ sm: 2, md: 12 }}
          py={4}
        >
          <SubmitButton w={"full"} data-testid={"submit-button"}>
            <T>Continue</T>
          </SubmitButton>
          <HStack>
            <Text layerStyle={"muted"}>
              <T>Remember your password?</T>
            </Text>
            <Link as={RouterLink} to={"/login"} data-testid={"login-link"}>
              Login
            </Link>
          </HStack>
          <HStack>
            <Text layerStyle={"muted"}>
              <T>New to Preventure?</T>
            </Text>
            <Link as={RouterLink} to={"/sign-up"} data-testid={"signup-link"}>
              Sign up
            </Link>
          </HStack>
        </Stack>
      </Stack>
    </ValidatedForm>
  );
}
